/**
 * LinkedFiles.jsx - the record's numbers that have a labeled file in its Doc
 * Hub, as links that open the file: the quote, the PO, each invoice, and the
 * RFQ linked in the RFQ folder. The project page showed these numbers as
 * links; now that its fields are editable boxes the links sit together in
 * one line at the top of the Summary. A number is a link ONLY when a labeled
 * file matches it, and nothing shows when none does - a link here never
 * leads nowhere.
 *
 * `docs` is the record's attachment list; `quote`, `po` and `invoices` are
 * the numbers as the form holds them now; `base` is the tab's API base.
 */
import { findDocFor, findLinkedRfq, previewAttachment } from '../../utils/attachmentPreview';
import { splitInvoiceNumbers } from '../../utils/docSuggest';

const linkSt = {
  background: 'none',
  border: 'none',
  padding: 0,
  cursor: 'pointer',
  font: 'inherit',
  color: '#1565C0',
  fontWeight: 700,
  textDecoration: 'underline',
  textUnderlineOffset: 2,
};

/** [{ key, text, doc }] - the links to show, in reading order. PURE. */
export function linkedFiles(docs, { quote, po, invoices } = {}) {
  const list = Array.isArray(docs) ? docs : [];
  if (!list.length) return [];
  const out = [];
  const q = String(quote || '').trim();
  const p = String(po || '').trim();
  const qd = q && findDocFor(list, 'quote', q);
  if (qd) out.push({ key: `quote-${q}`, text: `Quote ${q}`, doc: qd });
  const rfq = findLinkedRfq(list);
  if (rfq) out.push({ key: `rfq-${rfq.id}`, text: rfq.doc_ref ? `RFQ ${rfq.doc_ref}` : rfq.filename, doc: rfq });
  const pd = p && findDocFor(list, 'po', p);
  if (pd) out.push({ key: `po-${p}`, text: `PO ${p}`, doc: pd });
  for (const n of splitInvoiceNumbers(invoices)) {
    const d = findDocFor(list, 'invoice', n);
    if (d) out.push({ key: `inv-${n}`, text: `Invoice ${n}`, doc: d });
  }
  return out;
}

export default function LinkedFiles({ docs, quote, po, invoices, base }) {
  const links = linkedFiles(docs, { quote, po, invoices });
  if (!links.length) return null;
  return (
    <div
      aria-label="Linked files"
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'baseline',
        gap: '4px 14px',
        padding: '8px 14px',
        marginBottom: 14,
        background: '#fff',
        border: '1px solid #E5E9DD',
        borderRadius: 10,
        fontSize: 11.5,
      }}
    >
      <span style={{ fontSize: 9.5, fontWeight: 800, color: '#888', letterSpacing: 1.2, textTransform: 'uppercase' }}>Files</span>
      {links.map((l) => (
        <button key={l.key} type="button" style={linkSt} title={`Open ${l.doc.filename}`} onClick={() => previewAttachment(l.doc.id, base)}>
          {l.text}
        </button>
      ))}
    </div>
  );
}
